/**
 * PDP hero enhancements (Figma 761:1302).
 *  <ma-thumb-rail>  — vertical thumbnail rail on desktop: up/down arrows, wheel/keyboard
 *                     scrolling, active thumb kept in view. Media switching itself is
 *                     Dawn's media-gallery.js (thumbnail buttons carry data-target).
 *  accordion        — <details> rows open/close with a height tween instead of a jump.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('ma-thumb-rail')) {
    class MaThumbRail extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.thumbnail-list');
        this.slider = this.querySelector('.thumbnail-slider');
        if (!this.list || !this.slider) return;
        this.mql = window.matchMedia('(min-width: 990px)');

        const mk = (dir, label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = `ma-thumb-rail__arrow ma-thumb-rail__arrow--${dir}`;
          b.setAttribute('aria-label', label);
          b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';
          b.addEventListener('click', () => this.step(dir === 'up' ? -1 : 1));
          return b;
        };
        this.up = mk('up', 'Previous images');
        this.down = mk('down', 'Next images');
        this.slider.prepend(this.up);
        this.slider.append(this.down);

        this.list.addEventListener('scroll', () => this.sync(), { passive: true });
        this.list.addEventListener('click', (e) => {
          const item = e.target.closest('.thumbnail-list__item');
          if (item && this.mql.matches) this.reveal(item);
        });
        // keep the active thumb in view after variant changes re-render the gallery
        new MutationObserver(() => { this.sync(); this.ring(); }).observe(this.list, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });
        window.addEventListener('resize', () => { this.sync(); this.ring(true); });
        this.sync();
        this.ring(true);
      }
      // a single ring glides to the active thumb (CSS spring curve) instead of borders snapping
      ring(instant) {
        if (!this.ringEl) {
          this.ringEl = document.createElement('span');
          this.ringEl.className = 'ma-thumb-ring is-instant';
          this.ringEl.setAttribute('aria-hidden', 'true');
          this.list.append(this.ringEl);
          this.list.classList.add('has-ma-ring');
          instant = true;
        }
        const btn = this.list.querySelector('.thumbnail[aria-current]');
        const r = this.ringEl;
        if (!btn || !btn.offsetWidth) { r.style.opacity = '0'; return; }
        r.classList.toggle('is-instant', !!instant);
        r.style.opacity = '1';
        r.style.width = `${btn.offsetWidth}px`;
        r.style.height = `${btn.offsetHeight}px`;
        let x = 0, y = 0; // offsets up to the (position:relative) list; thumb items are positioned too
        for (let el = btn; el && el !== this.list; el = el.offsetParent) { x += el.offsetLeft; y += el.offsetTop; }
        r.style.transform = `translate(${x}px, ${y}px)`;
        if (instant) requestAnimationFrame(() => r.classList.remove('is-instant'));
      }
      step(dir) {
        const max = this.list.scrollHeight - this.list.clientHeight;
        const atEnd = dir > 0 ? this.list.scrollTop >= max - 1 : this.list.scrollTop <= 0;
        if (atEnd && window.MaLoop) return window.MaLoop.jump(this.list, { top: dir > 0 ? 0 : max }); // loop round
        const h = (this.list.querySelector('.thumbnail-list__item')?.offsetHeight || 100) + 16;
        this.list.scrollBy({ top: dir * h * 2, behavior: reduce ? 'auto' : 'smooth' });
      }
      reveal(item) {
        const top = item.offsetTop - this.list.offsetTop;
        const bottom = top + item.offsetHeight;
        if (top < this.list.scrollTop) this.list.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
        else if (bottom > this.list.scrollTop + this.list.clientHeight)
          this.list.scrollTo({ top: bottom - this.list.clientHeight, behavior: reduce ? 'auto' : 'smooth' });
      }
      sync() {
        const vertical = this.mql.matches;
        const overflow = this.list.scrollHeight - this.list.clientHeight > 2;
        this.classList.toggle('has-overflow', vertical && overflow);
        this.up.disabled = this.list.scrollTop <= 0; // down arrow never disables: at the bottom it loops to the top
      }
    }
    customElements.define('ma-thumb-rail', MaThumbRail);
  }

  // Swipe gallery: native scroll-snap does the physics (momentum, rubber-band); this adds
  // chevrons, mouse drag, thumbnail sync and a soft scale/fade on slides as they move.
  document.querySelectorAll('.ma-pdp-hero__media media-gallery').forEach((gallery) => {
    const list = gallery.querySelector('.product__media-list');
    const viewer = list && list.closest('slider-component');
    if (!list || !viewer) return;
    const slides = () => [...list.children].filter((li) => li.classList.contains('product__media-item') && li.offsetWidth);
    if (slides().length < 2) return;

    const chevron = (d) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
    const mk = (dir, label, d) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `ma-gallery__nav ma-gallery__nav--${dir}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = chevron(d);
      viewer.append(b);
      return b;
    };
    const prev = mk('prev', 'Previous image', 'm15 18-6-6 6-6');
    const next = mk('next', 'Next image', 'm9 18 6-6-6-6');

    // where slide i really rests (Dawn's mobile slider adds a 15px scroll-padding, so not offsetLeft)
    const pos = (s, i) => (window.MaLoop ? window.MaLoop.snapLeft(list, s[i]) : s[i].offsetLeft - s[0].offsetLeft);
    const index = () => {
      const s = slides();
      let best = 0;
      s.forEach((_, i) => { if (Math.abs(pos(s, i) - list.scrollLeft) < Math.abs(pos(s, best) - list.scrollLeft)) best = i; });
      return best;
    };
    const go = (i) => {
      const s = slides();
      i = Math.max(0, Math.min(s.length - 1, i));
      list.scrollTo({ left: pos(s, i), behavior: reduce ? 'auto' : 'smooth' });
    };
    // Endless: past either end it dissolves round to the other end (no rewind through
    // every image) and a soft "1 / n" cue says the gallery has looped.
    const step = (d) => {
      const s = slides();
      const n = s.length;
      const j = index() + d;
      if (j >= 0 && j < n) return go(j);
      const k = j < 0 ? n - 1 : 0;
      if (window.MaLoop) window.MaLoop.jump(list, { left: pos(s, k), host: viewer, text: `${k + 1} / ${n}` });
      else go(k);
    };
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    window.MaLoop && window.MaLoop.edges(list, { next: () => step(1), prev: () => step(-1) });

    let last = -1;
    const sync = () => {
      const s = slides();
      const w = list.clientWidth || 1;
      if (!reduce) {
        s.forEach((li, i) => {
          const d = Math.max(-1, Math.min(1, (pos(s, i) - list.scrollLeft) / w));
          const el = li.firstElementChild;
          if (el) {
            el.style.transform = d ? `scale(${1 - Math.abs(d) * 0.08})` : '';
            el.style.opacity = d ? String(1 - Math.abs(d) * 0.4) : '';
          }
        });
      }
      const i = index();
      if (i === last) return;
      last = i;
      s.forEach((li, j) => li.classList.toggle('is-active', j === i));
      const thumb = gallery.querySelector(`[data-target="${s[i].dataset.mediaId}"]`);
      const tlist = thumb && thumb.parentElement;
      if (!thumb) return;
      gallery.querySelectorAll('.thumbnail[aria-current]').forEach((b) => b.removeAttribute('aria-current'));
      thumb.querySelector('button')?.setAttribute('aria-current', 'true');
      // keep the thumb in view inside its own strip/rail, never by scrolling the page
      if (tlist.scrollHeight > tlist.clientHeight + 2) {
        const top = thumb.offsetTop - tlist.firstElementChild.offsetTop;
        if (top < tlist.scrollTop || top + thumb.offsetHeight > tlist.scrollTop + tlist.clientHeight)
          tlist.scrollTo({ top: top - tlist.clientHeight / 2 + thumb.offsetHeight / 2, behavior: 'smooth' });
      } else if (tlist.scrollWidth > tlist.clientWidth + 2) {
        // strip: always centre the active thumb (the browser clamps at the ends), so its neighbours stay in view
        tlist.scrollTo({ left: thumb.offsetLeft + thumb.offsetWidth / 2 - tlist.clientWidth / 2, behavior: reduce ? 'auto' : 'smooth' });
      }
    };
    let raf;
    list.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(sync); }, { passive: true });
    window.addEventListener('resize', sync);

    // mouse drag (touch already swipes natively); a flick of 40px+ moves one image
    let x0 = 0, left0 = 0, i0 = 0, moved = false, dragging = false;
    list.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true; moved = false; x0 = e.clientX; left0 = list.scrollLeft; i0 = index();
    });
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - x0;
      if (!moved && Math.abs(dx) > 5) { moved = true; list.classList.add('is-dragging'); }
      if (moved) list.scrollLeft = left0 - dx;
    });
    window.addEventListener('pointerup', (e) => {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      const dx = e.clientX - x0;
      list.classList.remove('is-dragging');
      if (Math.abs(dx) <= 40) return go(index());
      const j = i0 + (dx < 0 ? 1 : -1);
      j < 0 || j >= slides().length ? step(dx < 0 ? 1 : -1) : go(j);
    });
    list.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    list.addEventListener('dragstart', (e) => e.preventDefault());

    sync();
  });

  // Ingredients row: the INCI lists run to 40+ items, so lead with the named botanicals
  // as chips and tuck the full list behind its own toggle. Without JS the raw list shows.
  document.querySelectorAll('[data-ma-ingredients] .rte').forEach((rte) => {
    const out = document.createElement('div');
    out.className = 'ma-ingr';
    let title = null;
    rte.querySelectorAll('p').forEach((p) => {
      const text = p.textContent.trim();
      if (!text) return;
      const strong = p.querySelector('strong');
      if (strong && strong.textContent.trim() === text) { title = text; return; }
      const items = text.split(/,\s*(?![^()]*\))/).map((s) => s.trim()).filter(Boolean);
      if (items.length < 8) { out.append(p.cloneNode(true)); return; } // short copy: leave as-is
      const names = [...new Set(items.map((i) => (i.match(/\(([^)]+)\)/) || [])[1])
        .filter((n) => n && !/water|fragrance/i.test(n)))];
      const group = document.createElement('div');
      group.className = 'ma-ingr__group';
      group.innerHTML = `${title ? '<p class="ma-ingr__title"></p>' : ''}<p class="ma-ingr__label">Key botanicals</p><ul class="ma-ingr__chips"></ul><details class="ma-ingr__full"><summary>Full ingredient list (${items.length})</summary><p class="ma-ingr__inci"></p></details>`;
      if (title) group.querySelector('.ma-ingr__title').textContent = title;
      const ul = group.querySelector('.ma-ingr__chips');
      names.forEach((n) => { const li = document.createElement('li'); li.textContent = n; ul.append(li); });
      if (!names.length) group.querySelector('.ma-ingr__label').remove();
      group.querySelector('.ma-ingr__inci').textContent = text;
      out.append(group);
      title = null;
    });
    if (out.querySelector('.ma-ingr__group')) rte.replaceChildren(out);
  });

  // Animated <details>: tween the panel's height; native toggle still works without JS.
  document.querySelectorAll('.ma-pdp-hero__accordion-row').forEach((row) => {
    const summary = row.querySelector('summary');
    const panel = row.querySelector('.ma-pdp-hero__accordion-panel');
    if (!summary || !panel) return;
    summary.addEventListener('click', (e) => {
      if (reduce) return; // native jump is fine
      e.preventDefault();
      if (row.classList.contains('is-animating')) return;
      row.classList.add('is-animating');
      const opening = !row.open;
      if (opening) row.open = true;
      const from = opening ? 0 : panel.offsetHeight;
      const to = opening ? panel.offsetHeight : 0;
      const anim = panel.animate([{ height: `${from}px`, opacity: opening ? 0 : 1 }, { height: `${to}px`, opacity: opening ? 1 : 0 }], { duration: opening ? 420 : 320, easing: 'cubic-bezier(.32,.72,0,1)' }); // the site's sheet curve (ma-tokens --ma-ease-sheet)
      panel.style.overflow = 'hidden';
      anim.onfinish = () => {
        if (!opening) row.open = false;
        panel.style.overflow = '';
        row.classList.remove('is-animating');
      };
    });
  });
})();
